"use client";

import { useLayoutEffect } from "react";
import { usePathname, useSearchParams } from "@/lib/spa/navigation";
import {
  parseStockReturn,
  parseViaSymbol,
  stockReturnLabel,
  STOCK_RETURN_PARAM,
  STOCK_VIA_PARAM,
} from "@/lib/stock/briefHref";
import { applyPathSymbol } from "@/lib/stock/briefHandoff";
import {
  clearStockReturn,
  isStockHopDest,
  readStockReturn,
  sameReturnOrigin,
  type StockReturnOrigin,
} from "@/lib/stock/briefReturn";
import { BriefBack } from "./primitives";

function resolveOrigin(
  fromQuery: string | null,
  viaQuery: string | null,
): StockReturnOrigin | null {
  const stored = readStockReturn();
  const href = fromQuery ?? stored?.href ?? null;
  if (!href) return null;
  const symbol = viaQuery || stored?.symbol || "";
  return { href, symbol };
}

/**
 * Carries the originating ticker onto the page we just landed on (scopes
 * are isolated — Charting's AMD is not Fundamentals' store), and shows a
 * bar to go back. Derived during render so the bar is on the first paint
 * of Financials, not after an effect. The layout effect writes the dest
 * (and origin, when the Charting nav tab is the hop home) so Stock Detail
 * sees the symbol before the panes paint.
 */
export function StockReturnBack() {
  const pathname = usePathname();
  const search = useSearchParams();
  const fromQuery = parseStockReturn(search.get(STOCK_RETURN_PARAM));
  const viaQuery = parseViaSymbol(search.get(STOCK_VIA_PARAM));
  const origin = resolveOrigin(fromQuery, viaQuery);
  const atHome = Boolean(origin && sameReturnOrigin(origin.href, pathname));
  const onHopDest = isStockHopDest(pathname);

  useLayoutEffect(() => {
    const next = resolveOrigin(fromQuery, viaQuery);
    const via = next?.symbol ?? "";
    if (next && sameReturnOrigin(next.href, pathname)) {
      if (via) applyPathSymbol(next.href, via);
      clearStockReturn();
      document.documentElement.style.setProperty("--mpp-return-h", "0px");
      return;
    }
    if (via && (viaQuery || onHopDest)) applyPathSymbol(pathname, via);
    document.documentElement.style.setProperty(
      "--mpp-return-h",
      next && onHopDest ? "36px" : "0px",
    );
    return () => document.documentElement.style.setProperty("--mpp-return-h", "0px");
  }, [pathname, fromQuery, viaQuery, onHopDest]);

  if (!origin || atHome || !onHopDest) return null;

  return (
    <div
      className="flex items-center gap-2 border-b border-line bg-accent-soft px-4 py-1.5 xl:px-6 2xl:px-8"
      style={{ paddingLeft: "var(--mpp-sal)", paddingRight: "var(--mpp-sar)" }}
    >
      <BriefBack
        href={origin.href}
        onClick={() => {
          if (origin.symbol) applyPathSymbol(origin.href, origin.symbol);
          clearStockReturn();
        }}
      >
        {stockReturnLabel(origin.href, origin.symbol)}
      </BriefBack>
      <span className="text-[11px] text-ink-muted">Return to the stock details that opened this page.</span>
    </div>
  );
}
